import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import Button from 'components/Button'
import { MyInternalLink, MyLink } from 'components/MyLink'
import { useMedicareUrl } from 'hooks/useMedicareUrl'
import { CmsOpenLiveChatEvent } from 'events/customEvents/cmsOpenLiveChat'
import { ChatBlockedAlert } from 'components/accountHome/ChatBlockedAlert/ChatBlockedAlert'
import { SsaLink } from 'containers/Premiums/components/SsaLink'
import { Alert } from '@cmsgov/ds-medicare-gov'
import { useEvent } from 'hooks/useEvent'

const NoWrapSpan: React.FC = ({ children }) => {
  return <span className="my-u-whitespace-nowrap">{children}</span>
}

function LinkToBillInfo ({ children }: {children?: React.ReactNode}) {
  const { fireEvent } = useEvent('premiumsMakeAPayment')
  const url = useMedicareUrl(
    'https://www.medicare.gov/basics/forms-publications-mailings/mailings/costs-and-coverage/medicare-premium-bill',
    'https://es.medicare.gov/basics/forms-publications-mailings/mailings/costs-and-coverage/medicare-premium-bill'
  )
  return <MyLink
            href={url}
            target='_blank'
            onClick={({ linkText, linkUrl }) => {
              fireEvent('premiums_makeapayment_premiumbillcms_click', {
                linkText,
                linkUrl
              })
            }}>
            {children}
         </MyLink>
}

function LinkToPremiums ({ children }: {children?: React.ReactNode}) {
  const { fireEvent } = useEvent('premiumsMakeAPayment')
  return <MyInternalLink
           to="/premiums"
           onClick={({ linkText, linkUrl }) => {
             fireEvent('premiums_makeapayment_findouthowyoupay_click', {
               linkText,
               linkUrl
             })
           }}
           >{children}</MyInternalLink>
}

function OpenLiveChat ({ children, onClick }: {children?: React.ReactNode, onClick: () => void}) {
  const { fireEvent } = useEvent('premiumsMakeAPayment')
  return <Button
          className='ds-c-button ds-c-button--ghost ds-u-padding--0'
          onClick={() => {
            onClick()
            fireEvent('premiums_makeapayment_onlinelivechat_click')
          }}>{children}
         </Button>
}

export function IneligibleAlert ({ deferred }: {deferred?: boolean}) {
  const { t } = useTranslation('makeAPaymentPage')
  const { fireEvent } = useEvent('premiumsMakeAPayment')
  const [popupError, setPopupError] = useState(false)

  const handleLiveChat = () => {
    setPopupError(false)
    const event = new CmsOpenLiveChatEvent(true, (error) => {
      if (error) {
        setPopupError(true)
      }
    })
    document.dispatchEvent(event)
  }

  return (
     <>
      <Alert variation='error' heading={t('ineligibleAlert.heading')} className='ds-u-measure--wide'>
       {deferred && (
        <Trans t={t} i18nKey='ineligibleAlert.deferred'
          components={{
            link1: <SsaLink onClick={({ linkText, linkUrl }) => fireEvent('premiums_makeapayment_visitssaccount_click', { linkText, linkUrl })}/>,
            link2: <OpenLiveChat onClick={handleLiveChat} />,
            nowrap: <NoWrapSpan />
          }}
        />
       )}

      {!deferred && (
        <Trans t={t} i18nKey='ineligibleAlert.content'
          components={{
            link1: <LinkToBillInfo />,
            link2: <LinkToPremiums />,
            link3: <OpenLiveChat onClick={handleLiveChat} />,
            nowrap: <NoWrapSpan />
          }}
        />
      )}
      </Alert>

      {popupError && <ChatBlockedAlert />}
     </>
  )
}

export default IneligibleAlert
